/** Small helpers districts share: NPC looks and the common scatter pickers. */
import type { Look } from '../cosmetics.ts'
import type { Ground, Prop, PropKind } from '../defs.ts'
import type { ScatterRule } from './plot.ts'

export const look = (seed: string, shape: number, hue: number, tone: number, hat: Look['hat']): Look => ({ seed, shape, hue, tone, hat })

/** Weighted choice by a 0..1 roll: [[kind, weight], ...]. */
export function pickKind(roll: number, table: Array<[PropKind, number]>): PropKind {
  const total = table.reduce((n, [, w]) => n + w, 0)
  let r = roll * total
  for (const [k, w] of table) { if (r < w) return k; r -= w }
  return table[table.length - 1]![0]
}

const FLOWER_COLORS = ['#ff8fab', '#ffd166', '#b69cff', '#ff9e6b', '#ffffff']

/** A picker for scatter rules: trees, bushes and the odd flower bed. */
export function woodsy(table: Array<[PropKind, number]>) {
  return (roll: number): Prop => {
    const kind = pickKind(roll, table)
    return kind === 'flowers' ? { kind, x: 0, y: 0, color: FLOWER_COLORS[Math.floor(roll * 997) % FLOWER_COLORS.length]! } : { kind, x: 0, y: 0 }
  }
}

/** A perfect maze of `n`×`n` cells as hedge props, with a 3×3 clearing in the middle and an opening on top. */
export function maze(x0: number, y0: number, n: number, seed: number): { hedges: Prop[]; center: [number, number]; entrance: [number, number] } {
  const size = n * 2 + 1
  const wall = Array.from({ length: size }, () => new Array<boolean>(size).fill(true))
  let s = seed >>> 0
  const rand = () => ((s = (Math.imul(s, 1664525) + 1013904223) >>> 0) / 4294967296)
  const seen = new Set<string>()
  const stack: Array<[number, number]> = [[0, 0]]
  seen.add('0,0')
  wall[1]![1] = false
  while (stack.length) {
    const [cx, cy] = stack[stack.length - 1]!
    const next = ([[1, 0], [-1, 0], [0, 1], [0, -1]] as const).map(([dx, dy]) => [cx + dx, cy + dy] as [number, number]).filter(([x, y]) => x >= 0 && y >= 0 && x < n && y < n && !seen.has(`${x},${y}`))
    if (!next.length) { stack.pop(); continue }
    const [nx, ny] = next[Math.floor(rand() * next.length)]!
    seen.add(`${nx},${ny}`)
    wall[1 + cy * 2 + (ny - cy)]![1 + cx * 2 + (nx - cx)] = false
    wall[1 + ny * 2]![1 + nx * 2] = false
    stack.push([nx, ny])
  }
  const m = n // centre tile index (n*2+1 tiles; middle = n)
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) wall[m + dy]![m + dx] = false
  wall[0]![1] = false // the way in
  const hedges: Prop[] = []
  for (let y = 0; y < size; y++) for (let x = 0; x < size; x++) if (wall[y]![x] && !(x === m && y === m)) hedges.push({ kind: 'hedge', x: x0 + x, y: y0 + y })
  return { hedges, center: [x0 + m, y0 + m], entrance: [x0 + 1, y0] }
}

/**
 * A flat-decal scatter rule: leaves, petals, puddles, cracks… Decals live in the
 * cached floor layer, so they are free to draw and never block anyone.
 */
export function decals(variants: string[], density: number, on: Ground[], rect?: [number, number, number, number]): ScatterRule {
  return { density, on, rect, anywhere: true, pick: (roll) => ({ kind: 'decal', x: 0, y: 0, variant: variants[Math.floor(roll * variants.length)]! }) }
}

/** A row of the same prop from x0 to x1 (inclusive) at y, every `step` tiles. */
export function row(kind: PropKind, x0: number, x1: number, y: number, step = 1, extra: Partial<Prop> = {}): Prop[] {
  const out: Prop[] = []
  for (let x = x0; x <= x1; x += step) out.push({ kind, x, y, ...extra })
  return out
}

/** A column of the same prop from y0 to y1 (inclusive) at x, every `step` tiles. */
export function col(kind: PropKind, x: number, y0: number, y1: number, step = 1, extra: Partial<Prop> = {}): Prop[] {
  const out: Prop[] = []
  for (let y = y0; y <= y1; y += step) out.push({ kind, x, y, ...extra })
  return out
}
